import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { queryOptions, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import type { AuthStrategy } from "../../EditorConfig"

const SimpleRole = z.enum([
  "SuperUser",
  "Owner",
  "Administrator",
  "Manager",
  "Writer",
  "Readonly",
])
export type SimpleRole = z.infer<typeof SimpleRole>

export function isAdminRole(role: SimpleRole = "Readonly") {
  return role === "Administrator" || role === "SuperUser" || role === "Owner"
}

const roleLabels: Record<SimpleRole, string> = {
  SuperUser: "SuperUser",
  Owner: "Owner",
  Administrator: "Administrator",
  Manager: "Publisher",
  Writer: "Writer",
  Readonly: "Read-only",
}

export function getRoleLabel(role: SimpleRole) {
  return roleLabels[role]
}

const GetContributorRole = z.enum([
  SimpleRole.enum.Manager,
  SimpleRole.enum.Writer,
  // Readonly is not included in the response
])
type GetContributorRole = z.infer<typeof GetContributorRole>

const RolesPerLocale = z.record(GetContributorRole)
type RolesPerLocale = z.infer<typeof RolesPerLocale>

const RolesPerUser = z.record(
  z.object({
    rolesPerLocale: RolesPerLocale,
  }),
)
type RolesPerUser = z.infer<typeof RolesPerUser>

const RolesPerTeamSpace = z.record(
  z.object({
    rolesPerLocale: RolesPerLocale,
    rolesPerUser: RolesPerUser,
  }),
)
export type RolesPerTeamSpace = z.infer<typeof RolesPerTeamSpace>

const RepositoryScope = z.object({
  rolesPerUser: z.record(SimpleRole),
})
type RepositoryScope = z.infer<typeof RepositoryScope>

const LocaleScope = z.object({
  rolesPerUser: RolesPerUser,
})
type LocaleScope = z.infer<typeof LocaleScope>

const TeamSpaceScope = z.object({
  rolesPerLocale: RolesPerLocale.optional(),
  rolesPerUser: RolesPerUser.optional(),
  rolesPerTeamSpace: RolesPerTeamSpace.optional(),
})
type TeamSpaceScope = z.infer<typeof TeamSpaceScope>

const GetRolesResponse = z.object({
  repository: RepositoryScope.optional(),
  locale: LocaleScope.optional(),
  teamSpace: TeamSpaceScope.optional(),
})
export type GetRolesResponse = z.infer<typeof GetRolesResponse>

export type Scope = "repository" | "locale" | "teamSpace"

interface GetRolesArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  scopes?: Scope[]
  userIds?: string[]
  localeIds?: string[]
  teamSpaceIds?: string[]
  fetchEnabled: boolean
}

export function getRoles(args: GetRolesArgs): Promise<GetRolesResponse> {
  const {
    baseUrl,
    repository,
    authStrategy,
    scopes,
    userIds,
    localeIds,
    teamSpaceIds,
    fetchEnabled,
  } = args

  // NOTE: This is a temporary solution to disable roles fetch
  // for users who are not in the role api roles experiment.
  if (!fetchEnabled) {
    return Promise.resolve({})
  }

  const url = new URL("roles", baseUrl)
  url.searchParams.append("repository", repository)

  if (scopes?.length) {
    url.searchParams.set("scopes", scopes.join(","))
  }

  if (userIds?.length) {
    url.searchParams.set("userIds", userIds.join(","))
  }

  if (localeIds?.length) {
    url.searchParams.set("localeIds", localeIds.join(","))
  }

  if (teamSpaceIds?.length) {
    url.searchParams.set("teamSpaceIds", teamSpaceIds.join(","))
  }

  return request(url, {
    method: "GET",
    credentials: getRequestCredentials(authStrategy),
    okType: GetRolesResponse,
  })
}

export function getRolesOptions(args: GetRolesArgs) {
  return queryOptions({
    queryKey: ["roles", args],
    queryFn: () => getRoles(args),
  })
}

export type UpdateContributorRole = "Manager" | "Writer" | "Readonly"

export type UpdateRolesPerLocale = Record<string, UpdateContributorRole> | false

type UpdateRolesPerUser = Record<
  string,
  { rolesPerLocale?: UpdateRolesPerLocale }
>

export type UpdateTeamSpaceRoles =
  | {
      rolesPerLocale?: UpdateRolesPerLocale
      rolesPerUser?: UpdateRolesPerUser
    }
  | undefined

type UpdateRolesPerTeamSpace = Record<string, UpdateTeamSpaceRoles>

type UpdateRepositoryRole = "Administrator" | "Manager" | "Writer" | "Readonly"

export type UpdateRolesBody = {
  repository?: {
    rolesPerUser?: Record<string, UpdateRepositoryRole>
  }
  locale?: {
    rolesPerUser?: UpdateRolesPerUser
  }
  teamSpace?: {
    rolesPerLocale?: UpdateRolesPerLocale
    rolesPerUser?: UpdateRolesPerUser
    rolesPerTeamSpace?: UpdateRolesPerTeamSpace
  }
}

interface UpdateRolesArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  payload: UpdateRolesBody
}

export function updateRoles(args: UpdateRolesArgs): Promise<unknown> {
  const { baseUrl, repository, authStrategy, payload } = args

  const url = new URL("roles", baseUrl)
  url.searchParams.append("repository", repository)

  return request(url, {
    method: "PATCH",
    body: payload,
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}

export function useInvalidateRoles() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ["roles"] })
}

export const TeamSpace = z.object({
  id: z.string(),
  name: z.string(),
  customTypes: z.array(z.string()),
})

export type TeamSpace = z.infer<typeof TeamSpace>

const TeamSpaceUpsert = z.object({
  name: z.string(),
  customTypes: z.array(z.string()),
})

export type TeamSpaceUpsert = z.infer<typeof TeamSpaceUpsert>

const TeamSpacesResponse = z.object({ results: z.array(TeamSpace) })

export type TeamSpacesResponse = z.infer<typeof TeamSpacesResponse>

interface GetTeamSpacesArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
}

export function getTeamSpaces(
  args: GetTeamSpacesArgs,
): Promise<TeamSpacesResponse> {
  const { baseUrl, repository, authStrategy } = args

  const url = new URL("team-spaces", baseUrl)
  url.searchParams.append("repository", repository)

  return request(url, {
    method: "GET",
    credentials: getRequestCredentials(authStrategy),
    okType: TeamSpacesResponse,
  })
}

export function getTeamSpacesOptions(args: GetTeamSpacesArgs) {
  return queryOptions({
    queryKey: ["teamSpaces", args],
    queryFn: () => getTeamSpaces(args),
    select: (data) => data.results,
  })
}

export function useInvalidateTeamSpaces() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: ["teamSpaces"] })
}

interface CreateTeamSpaceArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  payload: TeamSpaceUpsert
}

export function createTeamSpace(args: CreateTeamSpaceArgs): Promise<TeamSpace> {
  const { baseUrl, repository, authStrategy, payload } = args

  const url = new URL("team-spaces", baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "POST",
    body: payload,
    credentials: getRequestCredentials(authStrategy),
    okType: TeamSpace,
  })
}

interface UpdateTeamSpaceArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  payload: TeamSpaceUpsert
  teamSpaceId: string
}

export function updateTeamSpace(args: UpdateTeamSpaceArgs): Promise<TeamSpace> {
  const { baseUrl, repository, authStrategy, payload, teamSpaceId } = args

  const url = new URL(`team-spaces/${teamSpaceId}`, baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "PUT",
    body: payload,
    credentials: getRequestCredentials(authStrategy),
    okType: TeamSpace,
  })
}

interface DeleteTeamSpaceArgs {
  baseUrl: URL
  repository: string
  authStrategy: AuthStrategy
  teamSpaceId: string
}

export function deleteTeamSpace(args: DeleteTeamSpaceArgs): Promise<unknown> {
  const { baseUrl, repository, authStrategy, teamSpaceId } = args

  const url = new URL(`team-spaces/${teamSpaceId}`, baseUrl)
  url.searchParams.append("repository", repository)
  return request(url, {
    method: "DELETE",
    credentials: getRequestCredentials(authStrategy),
    okType: z.unknown(),
  })
}
